import React, { useState, KeyboardEvent, useRef, useEffect } from 'react';
import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { coreService } from '../../services/coreService';

interface TagInputProps {
    label?: React.ReactNode;
    placeholder?: string;
    tags: string[];
    onChange: (tags: string[]) => void;
    error?: string;
    disabled?: boolean;
}

export const TagInput: React.FC<TagInputProps> = ({
    label,
    placeholder = 'Add a tag...',
    tags,
    onChange,
    error,
    disabled = false
}) => {
    const [inputValue, setInputValue] = useState('');
    const [validationMap, setValidationMap] = useState<Record<string, boolean>>({});
    const [isValidating, setIsValidating] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Validate tags when they change
    useEffect(() => {
        const unvalidatedTags = tags.filter(tag => validationMap[tag] === undefined);
        if (unvalidatedTags.length > 0) {
            validateBatch(unvalidatedTags);
        }
    }, [tags]);

    const validateBatch = async (keywords: string[]) => {
        setIsValidating(true);
        try {
            const response = await coreService.validateKeywords(keywords);
            if (response.success && response.data) {
                setValidationMap(prev => ({ ...prev, ...response.data }));
            }
        } catch (err) {
            console.error('Failed to validate keywords:', err);
            // Fallback: assume technical if API fails to avoid blocking UI
            const fallback: Record<string, boolean> = {};
            keywords.forEach(k => fallback[k] = true);
            setValidationMap(prev => ({ ...prev, ...fallback }));
        } finally {
            setIsValidating(false);
        }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addTag();
        } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
            removeTag(tags.length - 1);
        }
    };

    const addTag = () => {
        const trimmedInput = inputValue.trim();
        if (trimmedInput && !tags.includes(trimmedInput)) {
            onChange([...tags, trimmedInput]);
            setInputValue('');
        }
    };

    const removeTag = (indexToRemove: number) => {
        onChange(tags.filter((_, index) => index !== indexToRemove));
    };

    const handleBlur = () => {
        addTag();
    };

    // Focus input when clicking on container
    const handleContainerClick = () => {
        inputRef.current?.focus();
    };

    return (
        <div className="space-y-2">
            {label && (
                <div className="block text-xs font-bold text-text-secondary uppercase tracking-widest">
                    {label}
                </div>
            )}

            <div
                className={`
          min-h-[48px] flex flex-wrap items-center gap-2 px-3 py-2 
          border rounded-xl bg-white/5 transition-all cursor-text
          ${error
                        ? 'border-error/50 focus-within:ring-2 focus-within:ring-error/50 focus-within:border-error/60'
                        : 'border-white/10 focus-within:ring-2 focus-within:ring-primary/50 focus-within:border-primary/60 hover:border-white/20'
                    }
          ${disabled ? 'opacity-40 cursor-not-allowed bg-white/5' : ''}
        `}
                onClick={!disabled ? handleContainerClick : undefined}
            >
                {tags.map((tag, index) => {
                    const technical = validationMap[tag] !== false; // Default to true while pending or if undefined
                    const isPending = validationMap[tag] === undefined;

                    return (
                        <span
                            key={index}
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold transition-all
                                ${isPending ? 'opacity-70 animate-pulse' : ''}
                                ${technical
                                    ? 'bg-primary/20 text-primary border border-primary/30 shadow-[0_0_10px_rgba(255,107,0,0.1)]'
                                    : 'bg-error/20 text-error border border-error/30 animate-pulse-subtle'
                                }
                            `}
                            title={technical ? undefined : 'This keyword does not appear to be technical. Please use professional technical terms.'}
                        >
                            {isPending && <ArrowPathIcon className="h-3 w-3 mr-1.5 animate-spin" />}
                            {tag}
                            {!disabled && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeTag(index);
                                    }}
                                    className={`ml-1.5 h-3.5 w-3.5 rounded-full inline-flex items-center justify-center focus:outline-none transition-colors
                                        ${technical ? 'hover:bg-primary/30 text-primary/70' : 'hover:bg-error/30 text-error/70'}
                                    `}
                                >
                                    <XMarkIcon className="h-3 w-3" />
                                </button>
                            )}
                        </span>
                    );
                })}

                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    className="flex-1 min-w-[120px] outline-none bg-transparent text-sm text-white placeholder-text-secondary/50 font-medium"
                    placeholder={tags.length === 0 ? placeholder : ''}
                    disabled={disabled}
                />
            </div>

            {isValidating && (
                <div className="flex items-center gap-1.5 text-[10px] font-black text-primary uppercase tracking-wider animate-pulse">
                    <ArrowPathIcon className="h-3 w-3 animate-spin" />
                    Analyzing specialized technical stack...
                </div>
            )}

            {error && (
                <p className="text-sm font-medium text-error flex items-center gap-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
                    <span className="w-1 h-1 rounded-full bg-error" />
                    {error}
                </p>
            )}
        </div>
    );
};
